import { createAction, type QueuedAction } from "@/v2/hooks/useActionQueue";
import { durationToUnitsSeconds, parseEntryId } from "./entryId";
import { type ActionDeps, actingHeaders, refreshBoth } from "./types";

export type AddPayload = {
	id: number;
	date: string;
	matter: string;
	matter_id: string;
	description: string;
	duration: number;
	task: string;
	activity: string;
	shouldExport?: boolean;
	timekeeperId?: string | null;
};

/** Map the form payload to the v2 wire shape. Empty strings become null. */
export function toCreateBody(entry: AddPayload) {
	return {
		date: entry.date,
		unitsSeconds: durationToUnitsSeconds(entry.duration),
		description: entry.description,
		matterId: entry.matter_id || null,
		taskId: entry.task || null,
		activityId: entry.activity || null,
	};
}

function isExportPhaseError(err: unknown) {
	return (
		typeof err === "object" &&
		err !== null &&
		"phase" in err &&
		err.phase === "export"
	);
}

export function addAction(
	entry: AddPayload,
	deps: ActionDeps,
	onSuccess?: (newEntryId: number | null) => void,
	onEntryFailure?: (id: number, error: string) => void,
): QueuedAction {
	const { shouldExport } = entry;
	// Captured during execute() so the post-run onSuccess can forward it to
	// callers that want to chain follow-up work against the new row (e.g. the
	// /entries page's validated-export path).
	let newEntryId: number | null = null;
	return createAction({
		execute: async () => {
			const resp = await deps.makeApiRequest({
				endpoint: "/v2/entries",
				method: "POST",
				body: toCreateBody(entry),
				headers: actingHeaders(entry.timekeeperId),
			});
			if (resp.status === "ERROR")
				throw new Error(resp.error || "Request failed");
			if (resp.data?.id) {
				try {
					newEntryId = parseEntryId(resp.data.id);
				} catch {
					// Malformed id is non-fatal for the add itself — only the
					// chained export branch below needs the numeric id, and it
					// throws its own descriptive error.
				}
			}
			if (shouldExport) {
				if (newEntryId == null) {
					const err = resp.data?.id
						? new Error("Malformed entry id")
						: new Error("No entry ID returned");
					throw Object.assign(err, { phase: "export" });
				}
				// /records/exportRecords (v1) still takes numeric ids — strip the
				// "ent_" prefix here so the rest of the queue doesn't have to
				// learn about prefixed ids. The entry already persisted by this
				// point, so any failure here is an *export* failure — tag the
				// thrown error so the toast handler shows the right message
				// instead of "Failed to add entry" (which would tempt the user
				// to retry and create a duplicate).
				const exportResp = await deps.makeApiRequest({
					endpoint: "/records/exportRecords",
					method: "POST",
					body: { ids: [newEntryId] },
					headers: actingHeaders(entry.timekeeperId),
				});
				if (exportResp.status === "ERROR") {
					const msg = exportResp.error || "Export failed";
					onEntryFailure?.(newEntryId, msg);
					throw Object.assign(new Error(msg), { phase: "export" });
				}
			}
		},
		rollback: null,
		toast: {
			loading: "Adding entry…",
			success: shouldExport ? "Entry added and exported" : "Entry added",
			error: shouldExport
				? (err: unknown) =>
						isExportPhaseError(err)
							? "Entry added but export failed"
							: "Failed to add entry"
				: "Failed to add entry",
		},
		onSuccess: () => {
			void refreshBoth(deps);
			onSuccess?.(newEntryId);
		},
		onError: () => {
			void refreshBoth(deps);
		},
	});
}
